import React, { createContext, useContext, useMemo } from 'react';
import { isKOLocale } from '@marriott/shared/mi-helper-utils';

/**
 * Context type for locale-specific features
 */
export interface LocaleContextType {
  /** Whether to hide the Current Location feature (for Korean locale) */
  hideCurrentLocation: boolean;
  /** The current locale string */
  locale: string;
}

/**
 * LocaleContext for providing locale-specific configuration
 * Returns null when no provider exists (for backward compatibility)
 */
const LocaleContext = createContext<LocaleContextType | null>(null);

/**
 * Provider component for LocaleContext
 * Detects Korean locale and sets hideCurrentLocation accordingly
 */
export const LocaleProvider: React.FC<{
  locale: string;
  children: React.ReactNode;
}> = ({ locale, children }) => {
  const value = useMemo(() => {
    // Check if locale is Korean (ko-KR) or URL contains /ko/
    const isKorean = isKOLocale(locale) || locale.includes('/ko/');

    return {
      hideCurrentLocation: isKorean,
      locale,
    };
  }, [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

/**
 * Hook to access LocaleContext (nullable variant)
 * Returns null if no provider exists (for backward compatibility)
 * Use this when the LocaleProvider is optional
 */
export const useLocaleContext = (): LocaleContextType | null => {
  return useContext(LocaleContext);
};

/**
 * Hook to access LocaleContext (strict variant)
 * Throws an error if called outside a LocaleProvider
 * Use this when the LocaleProvider is required
 */
export const useRequiredLocaleContext = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (context === null) {
    throw new Error(
      'useRequiredLocaleContext must be used within a LocaleProvider. ' +
        'If the provider is optional, use useLocaleContext instead.'
    );
  }
  return context;
};

export { LocaleContext };
